import axios, { AxiosInstance, AxiosResponse, isAxiosError } from 'axios'
import { configuration } from '../configs'
import Navigation from '@App/navigation'
import { reloadInternal } from '@App/navigation/safe-navigation'
import { HttpError } from '@App/exceptions/HttpError'
import qs from 'qs'

// HttpError helper method, this will most likely want to be revisited at some point
export const throwHttpError = async (res: any) => {
    if (res.data) {
        // apparently we have two different error objects that are returned, this case covers the one I just found
        if (res.data.title) {
            throw new HttpError(res.data.title, res.data.status)
        } else {
            const errorMessageObject: { error: string; message: string; statusCode: string } = res.data
            throw new HttpError(errorMessageObject.message, res.status)
        }
    } else if (isAxiosError(res)) {
        throw new HttpError(res.message, res.code ?? 0)
    } else {
        throw new HttpError(res.statusText, res.status)
    }
}

export const standardHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
}

const standardParamSerializer = {
    serialize: (params: Record<string, any>) => qs.stringify(params, { arrayFormat: 'comma' }),
} as const

export const axiosClient: AxiosInstance = axios.create({
    baseURL: configuration.api,
    headers: standardHeaders,
    withCredentials: true,
    paramsSerializer: standardParamSerializer,
})

export const axiosStreamClient: AxiosInstance = axios.create({
    baseURL: configuration.api,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'X-Requested-With': 'XMLHttpRequest',
    },
    adapter: 'fetch',
    responseType: 'stream',
    withCredentials: true,
    paramsSerializer: standardParamSerializer,
})

export const unauthenticatedAxiosClient: AxiosInstance = axios.create({
    baseURL: configuration.api,
    headers: standardHeaders,
    withCredentials: true,
    paramsSerializer: standardParamSerializer,
})

const onRejectedHandler = (error: any) => {
    const res = error.response
    switch (res?.status) {
        case 503:
            // Just pass through 503s - these are handled by another interceptor
            return Promise.reject(error)
        case 401:
            if (!(
                error.config?.url?.endsWith('/login') ||
                error.config?.url?.endsWith('/mfa-confirm') ||
                error.config?.url?.endsWith('/user/me')
            )) {
                reloadInternal(Navigation.login.handleInactive())
            }
            break
        case 404:
            if (window.location.pathname.split('/').pop() !== 'view') {
                reloadInternal('/')
                break
            } else {
                return throwHttpError(res)
            }
        case 403:
            reloadInternal(Navigation.accessDenied.root())
            break
    }
    return throwHttpError(res ?? error)
}

axiosClient.interceptors.response.use((response: AxiosResponse) => response, onRejectedHandler)
axiosStreamClient.interceptors.response.use((response: AxiosResponse) => response, onRejectedHandler)
